/**
 * EB 商户端布局主题配置（对齐 view/mer store/modules/themeConfig.js）
 */
import { defineStore } from 'pinia';
import {
  applyBodyFilter,
  applyCrmebThemePalette,
} from '@/utils/applyCrmebThemePalette';
import { handleThemeStyle } from '@/utils/http/theme';
import router from '@/router';

export type CrmebLayoutType = 'defaults' | 'classic' | 'transverse' | 'columns';

export interface CrmebThemeConfigState {
  isDrawer: boolean;
  themeStyle: string;
  primary: string;
  menuBar: string;
  menuBarColor: string;
  columnsMenuBar: string;
  columnsMenuBarColor: string;
  topBar: string;
  topBarColor: string;
  isCollapse: boolean;
  isUniqueOpened: boolean;
  isFixedHeader: boolean;
  isShowLogo: boolean;
  isBreadcrumb: boolean;
  isBreadcrumbIcon: boolean;
  isTagsview: boolean;
  isTagsviewIcon: boolean;
  isFooter: boolean;
  isGrayscale: boolean;
  isInvert: boolean;
  tagsStyle: string;
  animation: string;
  columnsAsideStyle: string;
  columnsAsideLayout: string;
  layout: CrmebLayoutType;
}

const STORAGE_KEY = 'crmebThemeConfigPrev';

const defaultThemeConfig: CrmebThemeConfigState = {
  isDrawer: false,
  themeStyle: 'theme-1',
  primary: '#4073FA',
  menuBar: '#282c34',
  menuBarColor: '#eaeaea',
  columnsMenuBar: '#282c34',
  columnsMenuBarColor: '#e6e6e6',
  topBar: '#ffffff',
  topBarColor: '#606266',
  isCollapse: false,
  isUniqueOpened: true,
  isFixedHeader: true,
  isShowLogo: true,
  isBreadcrumb: true,
  isBreadcrumbIcon: false,
  isTagsview: true,
  isTagsviewIcon: false,
  isFooter: false,
  isGrayscale: false,
  isInvert: false,
  tagsStyle: 'tags-style-five',
  animation: 'opacitys',
  columnsAsideStyle: 'columns-card',
  columnsAsideLayout: 'columns-horizontal',
  layout: 'defaults',
};

function loadStoredConfig(): CrmebThemeConfigState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const merged = { ...defaultThemeConfig, ...JSON.parse(raw) };
      // 对齐 EB 管理端：一级分栏默认图标在文字左侧
      if (merged.columnsAsideLayout === 'columns-vertical') {
        merged.columnsAsideLayout = 'columns-horizontal';
      }
      return merged;
    }
  } catch {
    /* ignore */
  }
  return { ...defaultThemeConfig };
}

const useCrmebThemeConfigStore = defineStore('crmebThemeConfig', {
  state: (): { themeConfig: CrmebThemeConfigState } => ({
    themeConfig: loadStoredConfig(),
  }),
  actions: {
    persistConfig() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.themeConfig));
      } catch {
        /* ignore */
      }
    },
    applyCssVars() {
      const t = this.themeConfig;
      const palettePatch = applyCrmebThemePalette(t.themeStyle, t.layout);
      Object.assign(this.themeConfig, palettePatch);

      const root = document.documentElement;
      const isColumns = t.layout === 'columns';

      root.style.setProperty('--prev-color-primary', this.themeConfig.primary);
      root.style.setProperty('--prev-bg-topBar', this.themeConfig.topBar);
      root.style.setProperty('--prev-bg-topBarColor', isColumns ? '#515a6e' : this.themeConfig.topBarColor);
      root.style.setProperty('--prev-bg-columnsMenuBar', this.themeConfig.columnsMenuBar);
      root.style.setProperty('--prev-bg-columnsMenuBarColor', this.themeConfig.columnsMenuBarColor || '#e6e6e6');
      root.style.setProperty('--prev-bg-columnsMenuActiveColor', '#ffffff');

      if (isColumns) {
        root.style.setProperty('--prev-bg-menuBar', '#ffffff');
        root.style.setProperty('--prev-bg-menuBarColor', '#303133');
        root.style.setProperty('--prev-bg-menu-hover-ba-color', '#e8f4ff');
        root.style.setProperty('--prev-MenuActiveColor', this.themeConfig.primary);
        root.style.setProperty('--prev-border-color-lighter', '#ebeef5');
      } else if (t.layout === 'defaults') {
        // 选中高亮色由 applyCrmebThemePalette 写入（蓝黑=#4073FA），勿覆盖成灰色
        root.style.setProperty('--prev-bg-menuBar', this.themeConfig.menuBar);
        root.style.setProperty('--prev-bg-menuBarColor', this.themeConfig.menuBarColor);
        root.style.setProperty('--prev-border-color-lighter', '#ebeef5');
      }
      /* classic / transverse：applyCrmebThemePalette + eb-layout.scss 已写入变量，勿再用深色 menuBar 覆盖 */
      root.style.setProperty('--prev-bg-menu-active-color', this.themeConfig.primary);
      /* 对齐 EB setings.vue onColorPickerChange：生成 primary-light-* 供按钮 hover 等使用 */
      handleThemeStyle(this.themeConfig.primary);
      applyBodyFilter(t.isGrayscale, t.isInvert);
    },
    initTheme() {
      this.applyCssVars();
    },
    onConfigChange() {
      this.applyCssVars();
      this.persistConfig();
    },
    setThemeStyle(themeStyle: string) {
      this.themeConfig.themeStyle = themeStyle;
      this.onConfigChange();
    },
    setLayout(layout: CrmebLayoutType) {
      if (this.themeConfig.layout === layout) return false;
      this.themeConfig.layout = layout;
      if (layout === 'columns' || layout === 'defaults' || layout === 'classic') {
        this.themeConfig.isTagsview = true;
      }
      if (layout === 'defaults') {
        this.themeConfig.isCollapse = false;
      }
      this.themeConfig.isDrawer = false;
      this.onConfigChange();
      void import('@/store/modules/menu').then((mod) => {
        mod.default().syncLayoutByRoute(router.currentRoute.value);
      });
      return true;
    },
    setFilter(mode: 'grayscale' | 'invert', enabled?: boolean) {
      if (mode === 'grayscale') {
        this.themeConfig.isGrayscale = enabled ?? !this.themeConfig.isGrayscale;
        if (this.themeConfig.isGrayscale) this.themeConfig.isInvert = false;
      } else {
        this.themeConfig.isInvert = enabled ?? !this.themeConfig.isInvert;
        if (this.themeConfig.isInvert) this.themeConfig.isGrayscale = false;
      }
      this.onConfigChange();
    },
    resetConfig() {
      localStorage.removeItem(STORAGE_KEY);
      this.themeConfig = { ...defaultThemeConfig };
      window.location.reload();
    },
    toggleCollapse() {
      this.themeConfig.isCollapse = !this.themeConfig.isCollapse;
      this.persistConfig();
    },
    setCollapse(val: boolean) {
      this.themeConfig.isCollapse = val;
      this.persistConfig();
    },
    openSettingsDrawer() {
      this.themeConfig.isDrawer = true;
    },
    closeSettingsDrawer() {
      this.themeConfig.isDrawer = false;
      this.persistConfig();
    },
  },
});

export default useCrmebThemeConfigStore;
